import instance from '@src/api/axiosInstance';
import { useQuery } from '@tanstack/react-query';
import { defaultState, initNexaStore } from '../NexaStore';
import { trackQueryError } from '@src/utils/sensor/apptracker';
import { supportedEventTypes } from '../utils';

const useGetSession = ({ id }) => {
  const { isFetching, isError } = useQuery(
    ['nexa-session', id],
    async () => {
      try {
        const response = await instance.get('/nexa/session/' + id);
        const { events, state, entrypointParams } = parseSessionData(response.data);
        initNexaStore({ events, id, dashboardConfigMap: state, entrypointParams });
        return events;
      } catch (error) {
        initNexaStore({ ...defaultState, id });
        trackQueryError({ type: 'nexa-session-details', err: error });
      }
      return [];
    },
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 0,
      cacheTime: 0,
      notifyOnChangeProps: ['isFetching', 'isError', 'data'],
    },
  );
  return { isFetching, isError };
};

export default useGetSession;

const parseSessionData = (data: any) => {
  const sessionData = data?.session || { events: [], state: {} };
  const events: Array<{ [key: string]: any }> = [];

  // Extract entrypoint params from backend state
  const entrypointParams = sessionData.state?.entrypoint_params || null;

  sessionData.events.forEach((item) => {
    const { id, timestamp, content, author, error } = item;

    // Handle error events
    if (error) {
      events.push({
        id,
        author,
        type: 'error',
        value: {
          message: error.message || 'An error occurred. Please try again.',
          details: error.code || null,
        },
        timestamp,
      });
      return; // Skip processing content for error events
    }

    // Handle regular events with content
    if (content) {
      content.parts.forEach((part: any) => {
        for (const [key, value] of Object.entries(part)) {
          // Only process supported event types
          if (supportedEventTypes.includes(key)) {
            // Check if this part has a thought field
            const isThought = part.thought === true;

            events.push({
              id,
              author,
              type: key,
              value,
              timestamp,
              ...(isThought && { thought: true }),
            });
          }
        }
      });
    }
  });

  return { events, state: sessionData.state, entrypointParams };
};
